import { createStyles } from 'antd-style';
import { BRAND_PRODUCT_COLOR } from '@/constants/brand';
import { getAuthPalette } from './authColors';

/**
 * 登录 / 注册 / 授权共用 Auth 默认配色，不受目标平台展示色影响。
 * 面、字、线随 Ant Design 当前主题变化，Auth 品牌标识保留自己的颜色。
 */

/**
 * 拉丁文字用 Archivo，中文自然落到后面的中文字体 —— Archivo 没有中文字形，
 * 浏览器会逐字回落。设计稿里的品牌字就是这么排的。
 */
const LATIN = "'Archivo', 'Noto Sans SC', 'PingFang SC', sans-serif";

/**
 * 头像底色。同一个人每次进来颜色都一样，不同的人尽量不撞。
 *
 * 原来是 `id % 色板长度`，id 差一个色板长度的两个账号必然同色 —— 多账号并排时
 * 一眼看去就是两个一样的圈。改成对标识串做散列，色板也从 5 个加到 8 个。
 */
const AVATAR_COLORS = [
  '#1266E0', '#3F7A57', '#B60505', '#6B4EA8',
  '#0E6E7B', '#A65B12', '#2F5DA8', '#7A2E5C',
];

export const avatarColor = (seed: string | number | undefined | null) => {
  const text = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const useAuthStyles = createStyles(({ css, token }) => {
  const palette = getAuthPalette(token.colorBgContainer, token.colorFillQuaternary);
  // 全局通过 ConfigProvider 切换算法，antd-style 的独立 isDarkMode 并未同步。
  // 使用当前容器色判断明暗，使自定义文字色与实际背景一致。
  const isDarkMode = palette.isDark;
  // 稿子里的线和投影都是写死的值。antd 的 colorBorderSecondary 是 #f0f0f0，
  // 比这浅一大截，白底上等于没有边框；boxShadow* 也比稿子那两层重得多。
  // 暗色模式没有对应稿子，回落到 token。
  const line = token.colorBorderSecondary;
  const text = isDarkMode ? token.colorText : '#16202E';
  const muted = isDarkMode ? token.colorTextSecondary : '#6A7686';
  const wash = palette.wash;
  const errorBg = isDarkMode ? token.colorErrorBg : '#FDEEEE';
  const errorText = isDarkMode ? token.colorErrorText : '#B02A2F';
  const sheetShadow =
    '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)';

  return {
  page: css`
    position: relative;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 44px 20px;
    background: ${token.colorBgContainer};

    /* 手机上整屏就是那张纸，不要上下再露出灰底 */
    @media (max-width: 480px) {
      align-items: stretch;
      padding: 0;
      background: ${token.colorBgContainer};
    }
  `,

  lang: css`
    position: absolute;
    top: 16px;
    right: 20px;
    z-index: 2;
  `,

  sheet: css`
    width: min(420px, 100%);
    border: 1px solid ${line};
    border-radius: 16px;
    padding: 30px 28px 24px;
    background: ${token.colorBgContainer};
    box-shadow: ${sheetShadow};

    /* 手机上不要「小卡片浮在大留白里」，直接铺满整屏。 */
    @media (max-width: 480px) {
      width: 100%;
      border: none;
      border-radius: 0;
      box-shadow: none;
      padding: 56px 22px 28px;
    }
  `,

  mark: css`
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 20px;
  `,

  /* 顶栏用的版本：那 20px 是登录卡片里和标题之间的间距，
     放进一行高度固定的顶栏会把整个标识顶偏。 */
  markInline: css`
    display: flex;
    align-items: center;
    gap: 10px;
    /* 锁死高度：ProLayout 的 logo 槽位会跟着内容长，量出来能到 76px，
       比顶栏一行还高，整块就被撑变形了。 */
    height: 36px;
    line-height: 1;
    white-space: nowrap;

    /* 底板跟着字一起放大，比例才不别扭 */
    > span:first-of-type {
      height: 36px;
      width: 36px;
      border-radius: 10px;
    }
  `,

  /* logo 底板：角标要贴着右下角切进来，所以外面得有个能定位、能裁切的壳 */
  logoPlate: css`
    position: relative;
    box-sizing: border-box;
    height: 32px;
    width: 32px;
    flex: none;
    border-radius: 9px;
    background: ${token.colorBgContainer};
    border: 1px solid ${line};
    /* 裁掉角标溢出底板的部分，右下角自然跟着底板的圆角走 */
    overflow: hidden;
  `,

  logo: css`
    /*
      &&& 是为了压过 ProLayout：它给顶栏 logo 槽位写死了
      .ant-pro-global-header-logo img { height: 28px }，权重比单类高，
      图片会被压成 28px 顶在 32px 底板的上半截 —— 看着就是「logo 歪了」。
    */
    &&& {
      display: block;
      box-sizing: border-box;
      height: 100%;
      width: 100%;
    /* 图本身自带留白（100px 画布里内容只有 69x80），再加大内距就更显小，
       所以这里压到 3px。左右不对称是为了补 1px：这个 mark 左重右轻，
       几何居中看着会偏左。 */
      padding: 2px 1px 2px 3px;
      object-fit: contain;
    }
  `,

  /* 右下角的验证角标：贴死底板的右下角，只有左上角是圆的。
     外侧两条边交给底板的 overflow 裁切，自然跟着底板 11px 的圆角走 ——
     这样角标占的地方最小，对 logo 的遮挡也最少。 */
  logoBadge: css`
    position: absolute;
    /* 往外压 4px：底板的圆角会削掉外侧一圈，角标看起来更深地嵌在角里，
       露出的面积更小，也就更不挡 logo。压得越多露得越少，
       但对号要留得下 —— 15px 的块压掉 4px 还剩 11px，正好够放 8px 的勾。 */
    right: -3px;
    bottom: -3px;
    box-sizing: border-box;
    height: 13px;
    width: 13px;
    display: grid;
    place-items: center;
    /* 角标外侧那圈被底板圆角削掉了，对号按整块居中就会偏右下。
       右下垫上内距，让它在「实际露出来的那块」里居中。 */
    padding: 0 3px 3px 0;
    border-radius: 7px 0 0 0;
    background: ${BRAND_PRODUCT_COLOR};
    color: #fff;
  `,

  wordmark: css`
    font-family: ${LATIN};
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${text};
  `,

  /* 顶栏那版品牌字：14px 是配登录卡片里 23px 标题定的，顶栏没有那个标题，
     同样字号就显小。给到 18px，和 ProLayout 自带标题一个量级。 */
  wordmarkInline: css`
    font-family: ${LATIN};
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${text};
    /* 顶栏宽度紧张时宁可截断也不折行 —— 折行会把顶栏撑高 */
    white-space: nowrap;
  `,

  wordmarkProduct: css`
    /* 固定用 Auth 自己的蓝：这是产品身份，不该跟着目标业务平台的主色变 */
    color: ${BRAND_PRODUCT_COLOR};
  `,

  title: css`
    font-size: 23px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.3;
    color: ${text};
    margin: 0 0 5px;

    @media (max-width: 480px) {
      font-size: 26px;
    }
  `,

  subtitle: css`
    font-size: 13.5px;
    color: ${muted};
    margin: 0;
  `,

  stack: css`
    display: grid;
    gap: 15px;
  `,

  /* ── 账号列表 ── */
  acctList: css`
    display: grid;
    gap: 2px;
  `,

  acct: css`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    padding: 11px 12px;
    border: none;
    border-radius: 10px;
    background: transparent;
    cursor: pointer;
    transition: background 0.14s;

    &:hover,
    &:focus-visible {
      background: ${wash};
    }
    &:focus-visible {
      outline: 2px solid ${text};
      outline-offset: 1px;
    }
  `,

  avatar: css`
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    flex: none;
    color: #fff;
    font-weight: 600;
    font-size: 14px;
  `,

  acctName: css`
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    color: ${text};
  `,

  acctMail: css`
    font-family: ${LATIN};
    font-size: 12.5px;
    line-height: 1.35;
    color: ${muted};
  `,

  chevron: css`
    margin-left: auto;
    flex: none;
    color: ${muted};
  `,

  addAvatar: css`
    background: ${wash};
    color: ${muted};
  `,

  /* ── 账号胶囊（第二步 / 确认页的「更换」入口）── */
  pill: css`
    display: inline-flex;
    align-items: center;
    gap: 9px;
    justify-self: start;
    max-width: 100%;
    border: 1px solid ${line};
    border-radius: 999px;
    padding: 5px 14px 5px 6px;
    font-size: 13px;
    background: transparent;
    color: ${text};
    cursor: pointer;
    transition: background 0.14s;

    &:hover,
    &:focus-visible {
      background: ${wash};
    }
    &:focus-visible {
      outline: 2px solid ${text};
      outline-offset: 1px;
    }

    span[data-mail] {
      font-family: ${LATIN};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,

  pillAvatar: css`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    flex: none;
    color: #fff;
    font-weight: 600;
    font-size: 11px;
  `,

  pillSwap: css`
    color: ${palette.link};
    font-weight: 600;
    flex: none;
  `,

  /* ── 授权范围 ── */
  scopes: css`
    display: grid;
    gap: 10px;
  `,

  scope: css`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 13.5px;
    color: ${text};

    svg {
      color: ${muted};
      margin-top: 3px;
      flex: none;
    }
  `,

  fine: css`
    font-size: 12px;
    line-height: 1.6;
    color: ${muted};
    margin: 0;
  `,

  divider: css`
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: ${muted};

    &::before,
    &::after {
      content: '';
      height: 1px;
      flex: 1;
      background: ${line};
    }
  `,

  /* ── 按钮 ── */
  primary: css`
    &&& {
      width: 100%;
      height: 46px;
      border-radius: 10px;
      font-size: 15px;
      font-weight: 600;
      background: ${palette.primary};
      border-color: ${palette.primary};
      color: ${palette.primaryText};

      /* antd 会在两个汉字之间插空格，把「登录」拆成「登 录」 */
      > span {
        letter-spacing: 0;
      }

      &:not(:disabled):not(.ant-btn-loading):hover,
      &:not(:disabled):not(.ant-btn-loading):focus-visible {
        background: ${palette.hover};
        border-color: ${palette.hover};
        color: ${palette.hoverText};
      }

      &:not(:disabled):focus-visible {
        outline: 2px solid ${palette.link};
        outline-offset: 3px;
      }

      &:not(:disabled):not(.ant-btn-loading):active {
        background: ${palette.active};
        border-color: ${palette.active};
        color: ${palette.activeText};
      }

      &.ant-btn-loading {
        opacity: 1;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        border-color: ${token.colorBorder};
        color: ${token.colorTextDisabled};
        box-shadow: none;
      }
    }
  `,

  secondary: css`
    &&& {
      width: 100%;
      height: 46px;
      border-radius: 10px;
      font-size: 15px;
    }
  `,

  pair: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  `,

  foot: css`
    text-align: center;
    font-size: 13px;
    color: ${muted};

    a {
      color: ${palette.link};
      font-weight: 600;

      &:hover,
      &:focus-visible {
        color: ${palette.link};
        text-decoration: underline;
      }
    }
  `,

  /* 覆盖 antd 输入框，和上面的圆角/内距对齐 */
  field: css`
    &&& {
      border-radius: 10px;
      min-height: 46px;
      padding: 6px 13px;
      border-color: ${line};

      input {
        font-size: 14px;
      }

      .ant-input-prefix {
        margin-inline-end: 10px;
        color: ${muted};
      }

      /* 焦点用中性深色，不用品牌红 —— 红边框会被当成「这项填错了」 */
      &:focus,
      &:focus-within {
        border-color: ${text};
        box-shadow: 0 0 0 3px rgba(22, 32, 46, 0.08);
      }
    }
  `,

  /* 横幅和错误块：antd Alert 的圆角、配色和这套稿子对不上，自己画 */
  banner: css`
    display: flex;
    align-items: center;
    gap: 9px;
    border-radius: 9px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.5;
    background: ${wash};
    color: ${text};

    svg {
      flex: none;
    }
  `,

  alert: css`
    display: flex;
    align-items: center;
    gap: 9px;
    border-radius: 9px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.5;
    background: ${errorBg};
    color: ${errorText};

    svg {
      flex: none;
    }
  `,

  remember: css`
    font-size: 13.5px;
    color: ${muted};
  `,
  };
});
